/**
 * Modal de revisão do onboarding antes de ativar o aluno.
 *
 * Mostra:
 *   1. Dados de onboarding que o aluno preencheu (objetivo, nível,
 *      dias por semana, limitações, foco muscular)
 *   2. Sugestão IA derivada desse onboarding (resumo + dias do plano)
 *   3. Botões "Fechar" / "Ativar aluno"
 *
 * Props:
 *   - visible: controla mount/unmount
 *   - studentName: nome (ou email) do aluno, mostrado no subtítulo
 *   - onboardingData: dados do onboarding (vem de students.onboarding_data
 *     no banco). Pode ser null se o aluno foi criado manualmente.
 *   - suggestion: sugestão IA mais recente (passa do orquestrador, que
 *     consulta `onboarding_suggestions` com status pending OU accepted).
 *   - activating: loading do botão "Ativar"
 *   - onClose: fecha sem ativar
 *   - onConfirm: ativa o aluno (status → 'active')
 *
 * Decisões:
 *   - Se `onboardingData` for null, esconde a seção "Dados do aluno"
 *     mas ainda permite ativar — útil pra aluno criado direto pelo
 *     personal sem passar por onboarding.
 *   - "Foco muscular" vira tags rosadas — UX visual mais rica que lista.
 */

import { memo } from 'react';
import { View, Text, Modal, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { OnboardingData, OnboardingSuggestion } from '@/lib/types';
import { GOAL_LABEL, LEVEL_LABEL } from './helpers';

interface Props {
  visible: boolean;
  studentName: string;
  onboardingData: OnboardingData | null;
  suggestion: OnboardingSuggestion | null;
  activating: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function OnboardingModalImpl({
  visible, studentName, onboardingData, suggestion, activating, onClose, onConfirm,
}: Props) {
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <Text style={styles.title}>
              {onboardingData ? 'Ficha de Onboarding' : 'Liberar acesso'}
            </Text>
            <Text style={styles.subtitle}>
              {onboardingData
                ? `${studentName} — somente visualização`
                : `Deseja liberar o acesso do app para ${studentName}?`}
            </Text>

            {onboardingData && (
              <View style={styles.section}>
                <Text style={styles.sectionLabel}>Dados do aluno</Text>
                <View style={styles.row}>
                  <Text style={styles.rowLabel}>Objetivo</Text>
                  <Text style={styles.rowValue}>
                    {GOAL_LABEL[onboardingData.goal] ?? onboardingData.goal}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.rowLabel}>Nível</Text>
                  <Text style={styles.rowValue}>
                    {LEVEL_LABEL[onboardingData.level] ?? onboardingData.level}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.rowLabel}>Dias por semana</Text>
                  <Text style={styles.rowValue}>{onboardingData.days_per_week ?? '—'}x</Text>
                </View>
                {onboardingData.limitations ? (
                  <View style={styles.row}>
                    <Text style={styles.rowLabel}>Limitações</Text>
                    <Text style={[styles.rowValue, { flex: 2, textAlign: 'right' }]}>
                      {onboardingData.limitations}
                    </Text>
                  </View>
                ) : null}
                {onboardingData.muscle_focus && onboardingData.muscle_focus.length > 0 && (
                  <View style={{ marginTop: 8 }}>
                    <Text style={styles.rowLabel}>Foco muscular</Text>
                    <View style={styles.muscleTags}>
                      {onboardingData.muscle_focus.map((m, i) => (
                        <View key={i} style={styles.muscleTag}>
                          <Text style={styles.muscleTagText}>{m}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}
              </View>
            )}

            {suggestion && (
              <View style={styles.section}>
                <Text style={styles.sectionLabel}>Sugestão IA</Text>
                <View style={styles.suggestion}>
                  <Text style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
                    {suggestion.summary}
                  </Text>
                  {suggestion.plan_json.days.map((day, i) => (
                    <View key={i} style={styles.suggestionDay}>
                      <Text style={styles.suggestionDayName}>{day.name}</Text>
                      <Text style={styles.suggestionDayExs}>
                        {day.exercises.slice(0, 3).map(e => e.name).join(', ')}
                        {day.exercises.length > 3 ? '…' : ''}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </ScrollView>

          <View style={styles.actions}>
            <TouchableOpacity style={styles.close} onPress={onClose}>
              <Text style={styles.closeText}>Fechar</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.activate} onPress={onConfirm} disabled={activating}>
              <Text style={styles.activateText}>
                {activating ? 'Ativando...' : 'Ativar aluno'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 24,
    paddingBottom: 40,
    maxHeight: '85%',
  },
  title: { fontSize: 18, fontWeight: '700', color: '#1A1A1A', marginBottom: 4 },
  subtitle: { fontSize: 13, color: '#888', marginBottom: 16 },
  section: { marginTop: 14 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 6,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f5f5f5',
  },
  rowLabel: { fontSize: 13, color: '#888' },
  rowValue: { fontSize: 13, fontWeight: '600', color: '#1A1A1A' },
  muscleTags: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 6 },
  muscleTag: { backgroundColor: '#FFF0F2', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 },
  muscleTagText: { fontSize: 11, color: '#C8102E', fontWeight: '600' },
  suggestion: { backgroundColor: '#FFF0F2', borderRadius: 10, padding: 12, marginTop: 6 },
  suggestionDay: { backgroundColor: '#fff', borderRadius: 6, padding: 8, marginBottom: 4 },
  suggestionDayName: { fontSize: 11, fontWeight: '700', color: '#1A1A1A' },
  suggestionDayExs: { fontSize: 10, color: '#888', marginTop: 1 },
  actions: { flexDirection: 'row', gap: 10, marginTop: 16 },
  close: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    alignItems: 'center',
  },
  closeText: { fontSize: 14, fontWeight: '600', color: '#888' },
  activate: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#2E7D32',
    alignItems: 'center',
  },
  activateText: { fontSize: 14, fontWeight: '700', color: '#fff' },
});

export const OnboardingModal = memo(OnboardingModalImpl);
